import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/kfmApi";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, ChevronLeft, Users, BookOpen, MessageSquare, CornerDownRight, Check, LifeBuoy, Lightbulb, AlertTriangle, Wrench, HelpCircle, Mail, DollarSign, TrendingUp, Sparkles } from "lucide-react";
import { Input } from "@/components/ui/input";
import ContentEngine from "@/components/ContentEngine";
import FacebookComments from "@/components/FacebookComments";
import FacebookDMs from "@/components/FacebookDMs";
import CreditUsage from "@/components/CreditUsage";
import GrowthDashboard from "@/components/GrowthDashboard";
import HelpKbGaps from "@/components/HelpKbGaps";
import BetaTesters from "@/components/BetaTesters";
import BrandGuidelines from "@/components/BrandGuidelines";

function fmt(d) {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return "—"; }
}

const TRIAGE_META = {
  platform: { label: "Possible platform issue", icon: AlertTriangle, cls: "text-amber-300 bg-amber-500/10 border-amber-500/30" },
  app: { label: "Likely app / code", icon: Wrench, cls: "text-sky-300 bg-sky-500/10 border-sky-500/30" },
  unclear: { label: "Needs review", icon: HelpCircle, cls: "text-slate-400 bg-[#0B0F16] border-[#29344D]" },
};

const ADMIN_NOTICE_KEY = "owp_admin_notice_2026_09_media";

function AdminUpdateNotice() {
  const [dismissed, setDismissed] = useState(() => !!localStorage.getItem(ADMIN_NOTICE_KEY));
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  if (dismissed) return null;
  const dismiss = () => { localStorage.setItem(ADMIN_NOTICE_KEY, "1"); setDismissed(true); };
  const sendUpdateEmail = async () => {
    if (!window.confirm("Send the product-update email (new look, share buttons, pro/premium voices) to every non-admin user? This can only be undone by not sending it — there's no unsend.")) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/broadcast/product-update", { confirm: true });
      setResult(data);
      if (data.already_sent) toast.info(`Already sent earlier (${data.sent} delivered, ${data.failed} failed).`);
      else toast.success(`Sent to ${data.sent} users${data.failed ? `, ${data.failed} failed` : ""}.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't send the update email");
    }
    setSending(false);
  };
  return (
    <div data-testid="admin-update-notice" className="rounded-2xl border border-[#00E5FF]/20 bg-[#10222A]/60 p-4 flex items-start gap-3">
      <Sparkles className="w-4 h-4 text-[#00E5FF] mt-0.5 shrink-0" strokeWidth={1.75} />
      <div className="flex-1 space-y-2">
        <p className="text-sm text-slate-300">
          <span className="font-medium text-slate-100">Shipped recently:</span> a Media Library (upload/import your own
          photos & clips), True Vision for Shee (she can now actually look at and describe them), improvements
          across the Reels toolkit, one-tap share buttons on every Reflection post, and a Pro/Premium voice for
          reading studies aloud. Find them under Content &amp; Social → Setup.
        </p>
        <div className="flex items-center gap-3">
          <button data-testid="send-update-email-btn" onClick={sendUpdateEmail} disabled={sending}
                  className="h-8 px-3 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] text-xs font-medium flex items-center gap-1.5 disabled:opacity-50">
            {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" strokeWidth={1.75} />}
            Send update email to users
          </button>
          {result && !result.already_sent && (
            <span data-testid="send-update-email-result" className="text-xs text-slate-500">Sent {result.sent} · Failed {result.failed}</span>
          )}
        </div>
        <p className="text-[11px] text-slate-500">Only reaches real inboxes once this is deployed to production — here in preview it would only hit test/QA accounts. Guarded against double-sending.</p>
      </div>
      <button data-testid="admin-update-notice-dismiss" onClick={dismiss} className="text-slate-500 hover:text-slate-300 shrink-0 text-sm">
        Dismiss
      </button>
    </div>
  );
}

function Bubble({ m }) {
  const isAdmin = m.role === "admin";
  return (
    <div className={`flex ${isAdmin ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
        isAdmin ? "bg-[#10222A] text-slate-300 rounded-br-sm" : "bg-[#141B29] text-slate-300 rounded-bl-sm"
      }`}>
        <div className="flex items-center gap-1.5 mb-1 text-[10px] uppercase tracking-widest text-slate-400">
          {isAdmin ? "OpenWord AI" : "User"}
          {m.via === "email" && <Mail className="w-3 h-3" strokeWidth={1.5} />}
          {m.ai && <span data-testid="msg-ai-tag" className="inline-flex items-center gap-0.5 text-[#00E5FF]"><Sparkles className="w-3 h-3" strokeWidth={2} /> AI</span>}
        </div>
        {m.text}
      </div>
    </div>
  );
}

function FeedbackCard({ f, onReplied, onResolved }) {
  const [open, setOpen] = useState(false);
  const [msg, setMsg] = useState("");
  const [msgIsAI, setMsgIsAI] = useState(false);
  const [sending, setSending] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [replied, setReplied] = useState(f.replied);
  const [status, setStatus] = useState(f.status || (f.replied ? "resolved" : "open"));
  const [messages, setMessages] = useState(f.messages || []);
  const [pendingDraft, setPendingDraft] = useState(f.pending_draft || "");
  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolveNote, setResolveNote] = useState("");
  const [resolving, setResolving] = useState(false);
  const [simOpen, setSimOpen] = useState(false);
  const [simText, setSimText] = useState("");
  const [simulating, setSimulating] = useState(false);

  const isTech = f.category === "technical";
  const triage = f.triage;
  const tm = triage ? (TRIAGE_META[triage.classification] || TRIAGE_META.unclear) : null;

  const emergentDraft =
    "mailto:support@emergent.sh?subject=" +
    encodeURIComponent("OpenWord AI — possible platform issue") +
    "&body=" +
    encodeURIComponent(
      `Hi Emergent support,\n\nA user reported a possible platform issue with my deployed app (openwordai.com).\n\n` +
        `Summary: ${(triage && triage.summary) || f.text}\n\n` +
        `User's report:\n${f.text}\n\n` +
        `My job ID: [paste your job ID from the info (i) button, top-right of the chat]\n\nThank you.`
    );

  const refetch = async () => {
    try {
      const { data } = await api.get(`/admin/feedback/${f.id}`);
      setMessages(data.messages || []);
      setPendingDraft(data.pending_draft || "");
      setStatus(data.status);
      setReplied(data.replied);
    } catch (e) { /* silent */ }
  };

  const draftWithAI = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post(`/admin/feedback/${f.id}/draft`);
      setMsg(data.draft || "");
      setMsgIsAI(true);
      setOpen(true);
      toast.success("AI drafted a reply — review and send");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Draft failed");
    }
    setDrafting(false);
  };

  const usePending = () => { setMsg(pendingDraft); setMsgIsAI(true); setOpen(true); };

  const send = async () => {
    if (!msg.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/admin/feedback/${f.id}/reply`, { message: msg.trim(), ai: msgIsAI });
      setOpen(false);
      setMsg("");
      setMsgIsAI(false);
      if (onReplied) onReplied(f.id);
      await refetch();
      toast[data.emailed ? "success" : "warning"](
        data.emailed ? "Reply sent to their inbox" : "Reply saved, but the email could not be delivered."
      );
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSending(false);
  };

  const resolve = async () => {
    setResolving(true);
    try {
      const { data } = await api.post(`/admin/feedback/${f.id}/resolve`, { message: resolveNote.trim() });
      setResolveOpen(false);
      if (onResolved) onResolved(f.id);
      await refetch();
      toast.success(data.emailed ? "Marked resolved — user notified by email" : "Marked resolved (email not sent)");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setResolving(false);
  };

  const simulate = async () => {
    if (!simText.trim()) return;
    setSimulating(true);
    try {
      const { data } = await api.post(`/admin/feedback/${f.id}/simulate-inbound`, { text: simText.trim() });
      setSimText("");
      setSimOpen(false);
      await refetch();
      toast.success(data.action === "auto_sent"
        ? "Reply captured — AI auto-replied (idea/feedback)"
        : "Reply captured — AI draft held for your review (technical)");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Simulation failed");
    }
    setSimulating(false);
  };

  return (
    <div className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-5" data-testid={`feedback-card-${f.id}`}>
      <div className="flex items-center gap-2 flex-wrap mb-3">
        <span
          data-testid={`category-${f.id}`}
          className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border ${
            isTech ? "text-rose-300 bg-rose-500/10 border-rose-500/30" : "text-[#00E5FF] bg-[#10222A] border-[#00E5FF]/20"
          }`}
        >
          {isTech ? <LifeBuoy className="w-3 h-3" strokeWidth={2} /> : <Lightbulb className="w-3 h-3" strokeWidth={2} />}
          {isTech ? "Technical" : "Feedback"}
        </span>
        {tm && (
          <span data-testid={`triage-${f.id}`} className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border ${tm.cls}`}>
            <tm.icon className="w-3 h-3" strokeWidth={2} /> {tm.label}
          </span>
        )}
        {status === "needs_review" && (
          <span data-testid={`needs-review-${f.id}`} className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border text-purple-300 bg-purple-500/10 border-purple-500/30">
            <Sparkles className="w-3 h-3" strokeWidth={2} /> AI draft awaiting review
          </span>
        )}
        {status === "resolved" && (
          <span data-testid={`resolved-badge-${f.id}`} className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border text-emerald-400 bg-emerald-500/10 border-emerald-500/30">
            <Check className="w-3 h-3" strokeWidth={2} /> Resolved
          </span>
        )}
      </div>

      {/* Conversation thread */}
      <div data-testid={`thread-${f.id}`} className="space-y-2">
        {(messages.length ? messages : [{ role: "user", text: f.text, via: "form" }]).map((m, i) => (
          <Bubble key={i} m={m} />
        ))}
      </div>

      {tm && triage?.summary && (
        <p className="text-xs text-slate-500 mt-2 italic">AI triage: {triage.summary}</p>
      )}

      <div className="text-xs text-slate-500 mt-3 flex gap-2 flex-wrap items-center">
        <span className="text-[#00E5FF]">{f.name || "Anonymous"}</span>
        <span>·</span><span>{f.email || "no email"}</span>
        <span>·</span><span>{fmt(f.created_at)}</span>
      </div>

      {triage?.classification === "platform" && (
        <a
          data-testid={`emergent-draft-${f.id}`}
          href={emergentDraft}
          className="mt-3 inline-flex items-center gap-1.5 text-sm text-amber-300 hover:underline"
        >
          <Mail className="w-4 h-4" strokeWidth={1.5} /> Draft email to Emergent support
        </a>
      )}

      {/* Pending AI draft (technical replies held for review) */}
      {pendingDraft && !open && (
        <div data-testid={`pending-draft-${f.id}`} className="mt-3 rounded-xl border border-purple-500/30 bg-purple-500/10 p-3">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-purple-300 mb-1.5">
            <Sparkles className="w-3.5 h-3.5" strokeWidth={2} /> AI-suggested reply
          </div>
          <p className="text-sm text-slate-300 whitespace-pre-wrap">{pendingDraft}</p>
          <Button data-testid={`use-draft-${f.id}`} onClick={usePending} className="mt-2 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] h-8 px-4 text-xs">
            Review & edit
          </Button>
        </div>
      )}

      <div className="mt-3 flex items-center gap-4 flex-wrap">
        {!open && (
          <button data-testid={`reply-open-${f.id}`} onClick={() => setOpen(true)} className="text-sm text-[#00E5FF] hover:underline">
            {replied ? "Reply again" : "Reply"}
          </button>
        )}
        {!open && (
          <button data-testid={`draft-ai-${f.id}`} onClick={draftWithAI} disabled={drafting} className="text-sm text-[#00E5FF] hover:underline inline-flex items-center gap-1 disabled:opacity-50">
            {drafting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" strokeWidth={2} />} Draft with AI
          </button>
        )}
        {status !== "resolved" && !resolveOpen && (
          <button data-testid={`resolve-open-${f.id}`} onClick={() => setResolveOpen(true)} className="text-sm text-emerald-400 hover:underline">
            Mark resolved
          </button>
        )}
        <button data-testid={`simulate-open-${f.id}`} onClick={() => setSimOpen((v) => !v)} className="text-xs text-slate-500 hover:text-slate-400 inline-flex items-center gap-1">
          <Mail className="w-3.5 h-3.5" strokeWidth={1.5} /> Simulate email reply
        </button>
      </div>

      {open && (
        <div className="mt-3 space-y-2">
          <Textarea
            data-testid={`reply-text-${f.id}`}
            value={msg}
            onChange={(e) => setMsg(e.target.value)}
            placeholder={`Reply to ${f.name || f.email}…`}
            rows={4}
            className="rounded-xl border-[#29344D] resize-none focus-visible:ring-[#00E5FF]/30"
          />
          <div className="flex gap-2 flex-wrap">
            <Button data-testid={`reply-send-${f.id}`} onClick={send} disabled={sending} className="rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] h-9 px-5">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send reply"}
            </Button>
            <Button data-testid={`reply-draft-${f.id}`} onClick={draftWithAI} disabled={drafting} variant="outline" className="rounded-full h-9 px-5 border-[#00E5FF]/30 text-[#00E5FF] gap-1.5">
              {drafting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" strokeWidth={2} />} Redraft with AI
            </Button>
            <Button data-testid={`reply-cancel-${f.id}`} onClick={() => setOpen(false)} variant="outline" className="rounded-full h-9 px-5 border-[#29344D]">Cancel</Button>
          </div>
        </div>
      )}

      {simOpen && (
        <div className="mt-3 space-y-2 rounded-xl border border-dashed border-[#29344D] p-3 bg-[#0B0F16]/60">
          <p className="text-[11px] text-slate-400">Test the inbound flow: pretend the user emailed this reply back. Ideas auto-reply; technical reports draft-and-hold.</p>
          <Textarea
            data-testid={`simulate-text-${f.id}`}
            value={simText}
            onChange={(e) => setSimText(e.target.value)}
            placeholder="Type a customer's email reply…"
            rows={2}
            className="rounded-xl border-[#29344D] resize-none focus-visible:ring-[#00E5FF]/30 bg-[#0E131F]"
          />
          <div className="flex gap-2">
            <Button data-testid={`simulate-send-${f.id}`} onClick={simulate} disabled={simulating} className="rounded-full bg-[#1E273D] hover:bg-[#161D2F] text-white h-8 px-4 text-xs">
              {simulating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send as user"}
            </Button>
            <Button data-testid={`simulate-cancel-${f.id}`} onClick={() => setSimOpen(false)} variant="outline" className="rounded-full h-8 px-4 text-xs border-[#29344D]">Cancel</Button>
          </div>
        </div>
      )}

      {resolveOpen && (
        <div className="mt-3 space-y-2">
          <Textarea
            data-testid={`resolve-text-${f.id}`}
            value={resolveNote}
            onChange={(e) => setResolveNote(e.target.value)}
            placeholder="Optional note to the user (e.g. what was fixed)…"
            rows={2}
            className="rounded-xl border-[#29344D] resize-none focus-visible:ring-[#00E5FF]/30"
          />
          <div className="flex gap-2">
            <Button data-testid={`resolve-send-${f.id}`} onClick={resolve} disabled={resolving} className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white h-9 px-5">
              {resolving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Resolve & notify"}
            </Button>
            <Button data-testid={`resolve-cancel-${f.id}`} onClick={() => setResolveOpen(false)} variant="outline" className="rounded-full h-9 px-5 border-[#29344D]">Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-5 flex-1 min-w-[130px]">
      <Icon className="w-5 h-5 text-[#00E5FF] mb-3" strokeWidth={1.5} />
      <div className="text-3xl text-white">{value ?? "—"}</div>
      <div className="text-xs text-slate-500 mt-1 uppercase tracking-widest">{label}</div>
    </div>
  );
}

function PurgeTestData() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [purging, setPurging] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const loadPreview = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/purge-test-data/preview");
      setPreview(data);
      setConfirm(true);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't scan");
    }
    setLoading(false);
  };

  const purge = async () => {
    setPurging(true);
    try {
      const { data } = await api.post("/admin/purge-test-data");
      const n = data.deleted?.users || 0;
      toast.success(`Removed ${n} test account${n === 1 ? "" : "s"} and their data`);
      setConfirm(false);
      setPreview(null);
      setTimeout(() => window.location.reload(), 1200);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't purge");
    }
    setPurging(false);
  };

  const c = preview?.counts || {};

  return (
    <div data-testid="admin-purge" className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-6 space-y-4">
      <div>
        <h2 className="text-2xl text-white">Clean test data</h2>
        <p className="text-sm text-slate-400 mt-1">
          Removes every automated QA/test account (any <span className="font-mono text-xs">@example.com</span> email)
          and all of its studies, payments, credits and feedback — so your dashboard shows only real people.
          Your admin account and all real users are never touched.
        </p>
      </div>

      {!confirm && (
        <Button
          data-testid="purge-scan"
          onClick={loadPreview}
          disabled={loading}
          variant="outline"
          className="rounded-full border-[#3A4763] text-slate-300 hover:bg-[#0B0F16] px-6"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : "Scan for test data"}
        </Button>
      )}

      {confirm && preview && (
        <div className="space-y-4">
          <div data-testid="purge-summary" className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-4 text-sm text-amber-200">
            <p className="font-medium mb-2">This will permanently delete:</p>
            <ul className="space-y-0.5 text-amber-300">
              <li>{c.users || 0} test accounts</li>
              <li>{c.sessions || 0} study sessions · {c.sermons || 0} sermons · {c.prayer_journal || 0} prayer entries</li>
              <li>{c.payment_transactions || 0} payment records · {c.credit_log || 0} credit-log entries</li>
              <li>{c.feedback || 0} feedback items</li>
              <li>{(c.login_attempts || 0) + (c.signup_attempts || 0) + (c.intake_attempts || 0)} rate-limit log rows</li>
            </ul>
            {preview.emails?.length > 0 && (
              <p className="mt-2 text-xs text-amber-300">
                e.g. {preview.emails.slice(0, 3).join(", ")}{preview.emails.length > 3 ? `, +${preview.emails.length - 3} more` : ""}
              </p>
            )}
          </div>
          <div className="flex gap-3">
            <Button
              data-testid="purge-confirm"
              onClick={purge}
              disabled={purging}
              className="rounded-full bg-red-600 hover:bg-red-700 text-white px-6"
            >
              {purging ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : `Delete ${c.users || 0} test accounts`}
            </Button>
            <Button
              data-testid="purge-cancel"
              onClick={() => { setConfirm(false); setPreview(null); }}
              disabled={purging}
              variant="ghost"
              className="rounded-full text-slate-400 px-6"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function AnalyticsField({ label, hint, testid, value, onChange, placeholder }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-300 mb-1">{label}</label>
      <Input
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="rounded-xl border-[#29344D] focus-visible:ring-[#00E5FF]"
      />
      {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
    </div>
  );
}

function AnalyticsSettings() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(false);

  const load = async () => {
    setErr(false);
    try {
      const { data } = await api.get("/admin/analytics-config");
      setCfg(data);
    } catch {
      setErr(true);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/analytics-config", {
        posthog_key: cfg.posthog_key || "",
        posthog_host: cfg.posthog_host || "",
        ga4_id: cfg.ga4_id || "",
        gtm_id: cfg.gtm_id || "",
      });
      setCfg(data);
      toast.success("Analytics saved — reload the site to load your scripts");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save");
    }
    setSaving(false);
  };

  if (err)
    return (
      <div className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-6 text-center space-y-3">
        <p className="text-sm text-slate-400">{"Couldn't load analytics settings."}</p>
        <Button data-testid="analytics-retry" onClick={load} className="rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] px-6">Try again</Button>
      </div>
    );
  if (!cfg)
    return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 text-[#00E5FF] animate-spin" strokeWidth={1.5} /></div>;

  const set = (k) => (v) => setCfg((p) => ({ ...p, [k]: v }));

  return (
    <div data-testid="admin-analytics" className="space-y-6">
      <div className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-6 space-y-5">
        <div>
          <h2 className="text-2xl text-white">Your analytics</h2>
          <p className="text-sm text-slate-400 mt-1">
            Own your product data. Enter your own keys below and OpenWord AI loads them for every
            visitor — no Emergent proxy. Leave a field blank to disable that tool. Reload the site
            after saving.
          </p>
        </div>

        <div className="space-y-4">
          <div className="space-y-4 border-l-2 border-[#10222A] pl-4">
            <div className="text-xs font-semibold uppercase tracking-widest text-[#00E5FF]">PostHog</div>
            <AnalyticsField
              label="Project API Key"
              testid="analytics-posthog-key"
              value={cfg.posthog_key}
              onChange={set("posthog_key")}
              placeholder="phc_..."
              hint="The public Project API Key from Project settings (starts with phc_)."
            />
            <AnalyticsField
              label="API Host"
              testid="analytics-posthog-host"
              value={cfg.posthog_host}
              onChange={set("posthog_host")}
              placeholder="https://us.i.posthog.com"
              hint="US cloud: https://us.i.posthog.com · EU cloud: https://eu.i.posthog.com"
            />
          </div>

          <div className="space-y-4 border-l-2 border-[#10222A] pl-4">
            <div className="text-xs font-semibold uppercase tracking-widest text-[#00E5FF]">Google Analytics 4</div>
            <AnalyticsField
              label="Measurement ID"
              testid="analytics-ga4-id"
              value={cfg.ga4_id}
              onChange={set("ga4_id")}
              placeholder="G-XXXXXXXXXX"
              hint="Found under Admin → Data streams in GA4 (starts with G-)."
            />
          </div>

          <div className="space-y-4 border-l-2 border-[#10222A] pl-4">
            <div className="text-xs font-semibold uppercase tracking-widest text-[#00E5FF]">Google Tag Manager</div>
            <AnalyticsField
              label="Container ID"
              testid="analytics-gtm-id"
              value={cfg.gtm_id}
              onChange={set("gtm_id")}
              placeholder="GTM-XXXXXXX"
              hint="Your GTM container ID (starts with GTM-)."
            />
          </div>
        </div>

        <Button
          data-testid="analytics-save"
          onClick={save}
          disabled={saving}
          className="rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] px-8"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> : "Save analytics"}
        </Button>
      </div>

      <PurgeTestData />
    </div>
  );
}

export default function Admin() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [feedback, setFeedback] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [features, setFeatures] = useState(null);
  const [savingFeature, setSavingFeature] = useState(false);
  const [sendingDigest, setSendingDigest] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [tab, setTab] = useState("users");
  const [repliedIds, setRepliedIds] = useState(() => new Set());
  const [resolvedIds, setResolvedIds] = useState(() => new Set());
  const [fbFilter, setFbFilter] = useState("open");
  const [roleBusy, setRoleBusy] = useState("");
  const [mcBusy, setMcBusy] = useState("");
  const [mcSyncingAll, setMcSyncingAll] = useState(false);
  const [sortBy, setSortBy] = useState("");        // "" | status | credits | revenue
  const [sortDir, setSortDir] = useState("desc");
  const toggleSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setSortBy(key); setSortDir("desc"); }
  };
  const statusRank = (u) => {
    if (u.role === "admin") return -1;
    if (u.tier !== "free") return u.sub_cancel_at_period_end ? 3 : 4; // active subs rank highest
    if (u.trial_active) return 2;
    return 1;                                        // trial ended / plain free
  };
  const creditVal = (u) => (u.role === "admin" ? Infinity : u.trial_active ? 1e9 : (u.credits || 0) + (u.credits_topup || 0));
  const sortedUsers = (() => {
    if (!sortBy) return users;
    const val = (u) => (sortBy === "status" ? statusRank(u) : sortBy === "credits" ? creditVal(u) : (u.revenue || 0));
    const arr = [...users].sort((a, b) => val(a) - val(b));
    return sortDir === "desc" ? arr.reverse() : arr;
  })();
  const sortArrow = (key) => (sortBy === key ? (sortDir === "desc" ? " ↓" : " ↑") : "");

  const changeRole = async (u, role) => {
    if (role === "user" && !window.confirm(`Remove admin access from ${u.email}?`)) return;
    setRoleBusy(u.id);
    try {
      await api.put(`/admin/users/${u.id}/role`, { role });
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, role } : x)));
      toast.success(role === "admin" ? `${u.email} is now an admin` : `Removed admin from ${u.email}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update role");
    }
    setRoleBusy("");
  };

  const unreplied = feedback.filter((f) => !f.replied && !repliedIds.has(f.id) && !resolvedIds.has(f.id)).length;

  const syncMailchimp = async (u) => {
    setMcBusy(u.id);
    try {
      const { data } = await api.post(`/admin/users/${u.id}/mailchimp-sync`);
      setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, mailchimp_synced_at: data.synced_at } : x)));
      toast.success(`${u.email} synced to Mailchimp (${data.tags.join(", ")})`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Mailchimp sync failed");
    }
    setMcBusy("");
  };

  const syncMailchimpAll = async () => {
    setMcSyncingAll(true);
    try {
      const { data } = await api.post("/admin/users/mailchimp-sync-all");
      toast.success(`Synced ${data.synced}/${data.total} users to Mailchimp${data.failed ? ` · ${data.failed} failed` : ""}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Mailchimp sync failed");
    }
    setMcSyncingAll(false);
  };

  const load = async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [s, u, f, r, ff] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/users"),
        api.get("/admin/feedback"),
        api.get("/admin/revenue"),
        api.get("/admin/features"),
      ]);
      setStats(s.data);
      setUsers(u.data);
      setFeedback(f.data);
      setRevenue(r.data);
      setFeatures(ff.data);
    } catch {
      setLoadError(true);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#07090E]">
        <Loader2 className="w-6 h-6 text-[#00E5FF] animate-spin" strokeWidth={1.5} />
      </div>
    );

  if (loadError)
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[#07090E] px-6 text-center">
        <p className="text-slate-400">{"Couldn't load the dashboard."}</p>
        <Button data-testid="admin-retry" onClick={load} className="rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] px-6">
          Try again
        </Button>
      </div>
    );

  const isResolved = (f) => f.status === "resolved" || resolvedIds.has(f.id);
  const openCount = feedback.filter((f) => !isResolved(f)).length;
  const resolvedCount = feedback.filter(isResolved).length;
  const shownFeedback = feedback.filter((f) =>
    fbFilter === "all" ? true : fbFilter === "open" ? !isResolved(f) : isResolved(f)
  );

  const toggleFeature = async (name, enabled) => {
    setSavingFeature(true);
    try {
      const { data } = await api.put("/admin/features", { name, enabled });
      setFeatures(data);
      toast.success(enabled ? "Feature turned on for eligible users" : "Feature turned off");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSavingFeature(false);
  };

  const money = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

  const sendDigest = async () => {
    setSendingDigest(true);
    try {
      const { data } = await api.post("/admin/digest/send");
      toast.success(data.emailed ? "Digest emailed to you" : "Digest computed (email not configured)");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setSendingDigest(false);
  };

  const TAB_GROUPS = [
    { id: "people", label: "People", tabs: [["users", "Users"], ["beta", "Beta"], ["feedback", "Feedback"], ["help-gaps", "Help gaps"]] },
    { id: "growth", label: "Growth", tabs: [["growth", "Growth"], ["analytics", "Analytics"]] },
    { id: "money", label: "Revenue", tabs: [["revenue", "Revenue"], ["credits", "Credits"]] },
    { id: "content", label: "Content & Social", tabs: [["chat", "Chat with Shee"], ["manual", "Manual"], ["queue", "Queue"], ["track", "Track"], ["setup", "Setup"], ["comments", "FB Comments"], ["dms", "FB DMs"], ["brand", "Brand Guidelines"]] },
  ];
  const activeGroup = TAB_GROUPS.find((g) => g.tabs.some(([id]) => id === tab)) || TAB_GROUPS[0];

  return (
    <div className="min-h-screen bg-[#07090E]">
      <div className="max-w-5xl mx-auto px-6 py-10 space-y-8">
        <button
          data-testid="admin-back"
          onClick={() => navigate("/study")}
          className="flex items-center gap-1 text-sm text-slate-500 hover:text-[#00E5FF] transition-colors"
        >
          <ChevronLeft className="w-4 h-4" strokeWidth={1.5} /> Back to app
        </button>

        <header>
          <div className="text-xs font-medium uppercase tracking-[0.2em] text-[#00E5FF] mb-1">OpenWord AI · Admin</div>
          <h1 className="text-4xl text-white">Dashboard</h1>
        </header>

        <AdminUpdateNotice />

        <div className="flex gap-3 flex-wrap">
          <Stat icon={Users} label="Users" value={stats?.total_users} />
          <Stat icon={BookOpen} label="Studies" value={stats?.total_studies} />
          <Stat icon={MessageSquare} label="Feedback" value={stats?.total_feedback} />
        </div>

        <div className="space-y-3">
          {/* Category row */}
          <div className="flex gap-2 p-1 bg-[#141B29] rounded-xl w-full sm:w-fit overflow-x-auto no-scrollbar">
            {TAB_GROUPS.map((g) => {
              const isActive = g.tabs.some(([id]) => id === tab);
              const badge = g.id === "people" && unreplied > 0;
              return (
                <button
                  key={g.id}
                  data-testid={`admin-group-${g.id}`}
                  onClick={() => { if (!isActive) setTab(g.tabs[0][0]); }}
                  className={`shrink-0 px-4 sm:px-6 py-2 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 ${
                    isActive ? "bg-[#0E131F] text-[#00E5FF] shadow-sm" : "text-slate-400 hover:text-slate-300"
                  }`}
                >
                  {g.label}
                  {badge && !isActive && (
                    <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[#00E5FF] text-[#07090E] text-[11px] font-semibold">
                      {unreplied}
                    </span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Sub-tab row for the active category */}
          <div className="flex gap-2 flex-wrap">
            {activeGroup.tabs.map(([id, label]) => (
              <button
                key={id}
                data-testid={`admin-tab-${id}`}
                onClick={() => setTab(id)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-colors flex items-center gap-2 ${
                  tab === id
                    ? "bg-[#00E5FF] text-[#07090E] border-[#00E5FF]"
                    : "bg-[#0E131F] text-slate-400 border-[#29344D] hover:border-[#00E5FF]/50 hover:text-slate-300"
                }`}
              >
                {label}
                {id === "feedback" && unreplied > 0 && (
                  <span
                    data-testid="unreplied-badge"
                    className={`inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-semibold ${
                      tab === id ? "bg-[#0E131F] text-[#00E5FF]" : "bg-[#00E5FF] text-[#07090E]"
                    }`}
                  >
                    {unreplied}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {["chat", "manual", "queue", "track", "setup"].includes(tab) && <ContentEngine view={tab} onNavigate={setTab} />}

        {tab === "brand" && <BrandGuidelines />}

        {tab === "comments" && <FacebookComments />}

        {tab === "dms" && <FacebookDMs />}

        {tab === "growth" && <GrowthDashboard />}

        {tab === "beta" && <BetaTesters />}

        {tab === "help-gaps" && <HelpKbGaps />}

        {tab === "analytics" && <AnalyticsSettings />}

        {tab === "credits" && <CreditUsage />}

        {tab === "revenue" && (
          <div data-testid="admin-revenue" className="space-y-6">
            <div className="flex gap-3 flex-wrap">
              <Stat icon={TrendingUp} label="MRR" value={money(revenue?.mrr)} />
              <Stat icon={Users} label="Paid members" value={revenue?.active_paid} />
              <Stat icon={DollarSign} label="Top-up revenue" value={money(revenue?.topup_revenue)} />
            </div>

            <div className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-5 space-y-4" data-testid="revenue-breakdown">
              <div className="text-xs font-medium uppercase tracking-widest text-slate-500">Plans</div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {["free", "basic", "pro", "team"].map((t) => (
                  <div key={t} className="rounded-xl bg-[#07090E] border border-[#1E273D] p-3 text-center">
                    <div className="text-2xl text-white" data-testid={`rev-count-${t}`}>
                      {revenue?.tier_counts?.[t] ?? 0}
                    </div>
                    <div className="text-xs text-slate-500 capitalize mt-1">{t}</div>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm text-slate-400 pt-2 border-t border-[#1E273D]">
                <span>Subscription payments: <b className="text-slate-200">{revenue?.subscription_payments ?? 0}</b></span>
                <span>Top-ups sold: <b className="text-slate-200">{revenue?.topup_count ?? 0}</b></span>
                <span>Cancelling: <b className="text-slate-200">{revenue?.canceling ?? 0}</b></span>
                <span>Lapsed/past-due: <b className="text-slate-200">{revenue?.lapsed ?? 0}</b></span>
              </div>
            </div>

            <div className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-5 flex items-center justify-between gap-4" data-testid="admin-digest">
              <div>
                <div className="flex items-center gap-2 text-slate-200"><Mail className="w-4 h-4 text-[#00E5FF]" strokeWidth={1.5} /> Weekly digest</div>
                <p className="text-xs text-slate-500 mt-1">Auto-emailed every Monday 8am UTC. Send yourself a preview now.</p>
              </div>
              <button
                data-testid="send-digest-now"
                onClick={sendDigest}
                disabled={sendingDigest}
                className="shrink-0 rounded-full bg-[#00E5FF] hover:bg-[#00B8CC] text-[#07090E] px-5 py-2.5 text-sm font-medium transition-colors disabled:opacity-60"
              >
                {sendingDigest ? "Sending…" : "Send digest now"}
              </button>
            </div>

            <div className="bg-[#0E131F] rounded-2xl border border-[#1E273D] p-5 space-y-4" data-testid="admin-features">
              <div className="text-xs font-medium uppercase tracking-widest text-slate-500">Feature flags</div>
              <div className="flex items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-2 text-slate-200">
                    <Sparkles className="w-4 h-4 text-[#00E5FF]" strokeWidth={1.5} /> Study → Sermon
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Paid tool (Pro &amp; above). Turns a completed study into a preachable sermon outline for
                    40 credits. {features?.study_to_sermon ? "Currently live for eligible members." : "Currently off — turn on when you're ready to launch."}
                  </p>
                </div>
                <button
                  data-testid="feature-toggle-study_to_sermon"
                  role="switch"
                  aria-checked={!!features?.study_to_sermon}
                  aria-label="Toggle Study to Sermon feature"
                  disabled={savingFeature}
                  onClick={() => toggleFeature("study_to_sermon", !features?.study_to_sermon)}
                  className={`relative w-12 h-7 rounded-full transition-colors shrink-0 ${
                    features?.study_to_sermon ? "bg-[#00E5FF]" : "bg-[#3A4763]"
                  }`}
                >
                  <span
                    className={`absolute top-1 left-1 w-5 h-5 bg-[#0E131F] rounded-full transition-transform ${
                      features?.study_to_sermon ? "translate-x-5" : ""
                    }`}
                  />
                </button>
              </div>

              <div className="flex items-center justify-between gap-4 border-t border-[#1E273D] pt-4">
                <div>
                  <div className="flex items-center gap-2 text-slate-200">
                    <Sparkles className="w-4 h-4 text-[#00E5FF]" strokeWidth={1.5} /> Teach &amp; Share
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Turns a completed study + the reader&rsquo;s own reflection into a teaching resource
                    (sermon outline, teaching notes, devotional &amp; more), with PDF &amp; sharing.
                    {" "}{features?.teach_share ? "Currently live for members." : "Currently off — hidden from everyone until you turn it on."}
                  </p>
                </div>
                <button
                  data-testid="feature-toggle-teach_share"
                  role="switch"
                  aria-checked={!!features?.teach_share}
                  aria-label="Toggle Teach and Share feature"
                  disabled={savingFeature}
                  onClick={() => toggleFeature("teach_share", !features?.teach_share)}
                  className={`relative w-12 h-7 rounded-full transition-colors shrink-0 ${
                    features?.teach_share ? "bg-[#00E5FF]" : "bg-[#3A4763]"
                  }`}
                >
                  <span
                    className={`absolute top-1 left-1 w-5 h-5 bg-[#0E131F] rounded-full transition-transform ${
                      features?.teach_share ? "translate-x-5" : ""
                    }`}
                  />
                </button>
              </div>
            </div>
          </div>
        )}

        {tab === "users" && (
          <div data-testid="admin-users-table" className="bg-[#0E131F] rounded-2xl border border-[#1E273D] overflow-hidden">
            <div className="flex items-center justify-end px-4 pt-4">
              <Button
                data-testid="mailchimp-sync-all"
                onClick={syncMailchimpAll}
                disabled={mcSyncingAll}
                variant="outline"
                className="h-8 px-3 rounded-lg text-xs border-[#3A4763]"
              >
                {mcSyncingAll ? "Syncing…" : "Sync all to Mailchimp"}
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[980px]">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-widest text-slate-500 border-b border-[#1E273D]">
                    <th className="px-4 py-3 font-medium">Name</th>
                    <th className="px-4 py-3 font-medium">Email</th>
                    <th className="px-4 py-3 font-medium">Signed up via</th>
                    <th className="px-4 py-3 font-medium">Plan</th>
                    <th className="px-4 py-3 font-medium cursor-pointer select-none hover:text-slate-400"
                        onClick={() => toggleSort("status")} data-testid="sort-status">Status{sortArrow("status")}</th>
                    <th className="px-4 py-3 font-medium cursor-pointer select-none hover:text-slate-400"
                        onClick={() => toggleSort("credits")} data-testid="sort-credits">Credits{sortArrow("credits")}</th>
                    <th className="px-4 py-3 font-medium cursor-pointer select-none hover:text-slate-400"
                        onClick={() => toggleSort("revenue")} data-testid="sort-revenue">Revenue{sortArrow("revenue")}</th>
                    <th className="px-4 py-3 font-medium">Source</th>
                    <th className="px-4 py-3 font-medium">Joined</th>
                    <th className="px-4 py-3 font-medium">Last active</th>
                    <th className="px-4 py-3 font-medium text-center">Studies</th>
                    <th className="px-4 py-3 font-medium">Model</th>
                    <th className="px-4 py-3 font-medium">Mailchimp</th>
                    <th className="px-4 py-3 font-medium text-right">Role</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedUsers.map((u) => (
                    <tr key={u.id} className="border-b border-[#1E273D] last:border-0">
                      <td className="px-4 py-3 text-slate-200">
                        {u.name}
                        {u.role === "admin" && <span className="ml-2 text-[10px] uppercase text-[#00E5FF] bg-[#10222A] px-1.5 py-0.5 rounded">admin</span>}
                      </td>
                      <td className="px-4 py-3 text-slate-400">{u.email}</td>
                      <td className="px-4 py-3 text-slate-400" data-testid={`user-authprovider-${u.id}`}>
                        {u.auth_provider === "google" ? "Google" : "Email/password"}
                      </td>
                      <td className="px-4 py-3" data-testid={`user-tier-${u.id}`}>
                        <span className={`text-[11px] font-medium uppercase px-2 py-0.5 rounded-full ${
                          u.tier === "pro" ? "bg-[#10222A] text-[#00E5FF]"
                          : u.tier === "team" ? "bg-indigo-500/10 text-indigo-400"
                          : u.tier === "basic" ? "bg-amber-500/10 text-amber-400"
                          : "bg-[#141B29] text-slate-400"}`}>
                          {u.tier || "free"}
                        </span>
                      </td>
                      <td className="px-4 py-3" data-testid={`user-status-${u.id}`}>
                        {u.role === "admin" ? (
                          <span className="text-xs text-slate-500">—</span>
                        ) : u.tier !== "free" ? (
                          u.sub_cancel_at_period_end ? (
                            <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400"
                              title={u.sub_current_period_end ? `Ends ${fmt(u.sub_current_period_end)}` : ""}>
                              Cancelling{u.sub_current_period_end ? ` · ${fmt(u.sub_current_period_end)}` : ""}
                            </span>
                          ) : (
                            <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-green-500/10 text-green-300">
                              {u.sub_status === "past_due" ? "Past due" : "Active"}
                            </span>
                          )
                        ) : u.trial_active ? (
                          <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#10222A] text-[#00E5FF]">
                            Trial · {u.trial_days_left}d left
                          </span>
                        ) : (
                          <span className="text-[11px] font-medium px-2 py-0.5 rounded-full bg-[#141B29] text-slate-400">
                            Trial ended
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3" data-testid={`user-credits-${u.id}`}>
                        {u.role === "admin" ? (
                          <span className="text-xs text-slate-500">Unlimited</span>
                        ) : (
                          <span className="text-xs text-slate-300">
                            {u.trial_active ? <span className="text-[#00E5FF]">Unmetered</span> : <>{u.credits}</>}
                            {u.credits_topup > 0 && (
                              <span className="ml-1.5 text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400"
                                title={`${u.credits_topup} top-up credits remaining` + (u.topups_purchased ? ` · ${u.topups_purchased} top-up purchase(s)` : "")}>
                                +{u.credits_topup} topup
                              </span>
                            )}
                            {u.credits_topup === 0 && u.topups_purchased > 0 && (
                              <span className="ml-1.5 text-[10px] text-slate-500" title={`${u.topups_purchased} top-up purchase(s), all used`}>
                                ({u.topups_purchased} bought)
                              </span>
                            )}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3" data-testid={`user-revenue-${u.id}`}>
                        {u.revenue > 0 ? (
                          <span className="text-xs font-medium text-slate-200"
                            title={`${u.purchases} paid ${u.purchases === 1 ? "payment" : "payments"} lifetime`}>
                            {({ gbp: "£", usd: "$", eur: "€" }[u.currency] || "$")}{u.revenue.toFixed(2)}
                          </span>
                        ) : <span className="text-xs text-slate-500">—</span>}
                      </td>
                      <td className="px-4 py-3" data-testid={`user-source-${u.id}`}>
                        {u.utm_source ? (
                          <span className="text-xs text-slate-400" title={u.utm_campaign ? `Campaign: ${u.utm_campaign}` : ""}>
                            {u.utm_source === "facebook" ? "Facebook"
                              : u.utm_source.charAt(0).toUpperCase() + u.utm_source.slice(1)}
                            {u.utm_campaign ? <span className="text-slate-500"> · {u.utm_campaign.length > 16 ? u.utm_campaign.slice(0, 16) + "…" : u.utm_campaign}</span> : null}
                          </span>
                        ) : <span className="text-xs text-slate-500">Direct</span>}
                      </td>
                      <td className="px-4 py-3 text-slate-400">{fmt(u.created_at)}</td>
                      <td className="px-4 py-3 text-slate-400">{fmt(u.last_active)}</td>
                      <td className="px-4 py-3 text-center text-slate-200">{u.studies}</td>
                      <td className="px-4 py-3 text-slate-400">{u.model}</td>
                      <td className="px-4 py-3" data-testid={`user-mailchimp-${u.id}`}>
                        <button
                          data-testid={`mailchimp-sync-${u.id}`}
                          onClick={() => syncMailchimp(u)}
                          disabled={mcBusy === u.id}
                          title={u.mailchimp_synced_at ? `Last synced ${fmt(u.mailchimp_synced_at)}` : "Never synced"}
                          className="text-xs font-medium text-[#00E5FF] hover:text-[#00B8CC] transition-colors disabled:opacity-50"
                        >
                          {mcBusy === u.id ? "…" : u.mailchimp_synced_at ? "Re-sync" : "Sync"}
                        </button>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {u.role === "admin" ? (
                          <button
                            data-testid={`demote-${u.id}`}
                            disabled={roleBusy === u.id}
                            onClick={() => changeRole(u, "user")}
                            className="text-xs font-medium text-slate-500 hover:text-red-500 transition-colors disabled:opacity-50"
                          >
                            {roleBusy === u.id ? "…" : "Remove admin"}
                          </button>
                        ) : (
                          <button
                            data-testid={`promote-${u.id}`}
                            disabled={roleBusy === u.id}
                            onClick={() => changeRole(u, "admin")}
                            className="text-xs font-medium text-[#00E5FF] hover:text-[#00B8CC] transition-colors disabled:opacity-50"
                          >
                            {roleBusy === u.id ? "…" : "Make admin"}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "feedback" && (
          <div data-testid="admin-feedback-list" className="space-y-4">
            <div className="flex gap-2 flex-wrap">
              {[["open", "Open", openCount], ["resolved", "Resolved", resolvedCount], ["all", "All", feedback.length]].map(
                ([id, label, count]) => (
                  <button
                    key={id}
                    data-testid={`fb-filter-${id}`}
                    onClick={() => setFbFilter(id)}
                    className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
                      fbFilter === id
                        ? "bg-[#10222A] border-[#00E5FF]/30 text-[#00E5FF]"
                        : "bg-[#0E131F] border-[#29344D] text-slate-400 hover:border-[#00E5FF]/30"
                    }`}
                  >
                    {label} <span className="ml-1 text-xs opacity-70">{count}</span>
                  </button>
                )
              )}
            </div>
            <div className="space-y-3">
              {shownFeedback.length === 0 && (
                <p className="text-sm text-slate-500 py-6 text-center">
                  {fbFilter === "open"
                    ? "Nothing open — you're all caught up."
                    : fbFilter === "resolved"
                    ? "No resolved items yet."
                    : "No feedback yet."}
                </p>
              )}
              {shownFeedback.map((f) => (
                <FeedbackCard key={f.id} f={f}
                  onReplied={(id) => setRepliedIds((p) => new Set(p).add(id))}
                  onResolved={(id) => setResolvedIds((p) => new Set(p).add(id))} />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
